import type { SendChannel } from "./SendLinkCard/SendLinkCard";

/**
 * Adapters between the narrative send-card's `SendChannel` ("text" | "email") and the shapes the
 * real send-link + guided-browsing seams expect. Mirrors the legacy SendLink / GuidedBrowsingContent
 * formatting (`+1` + digits for SMS) so the new forms hit the same backend contract.
 */

/** `useHandleSendLink` comm type. */
export const channelToCommType = (channel: SendChannel): "SMS" | "EMAIL" => (channel === "text" ? "SMS" : "EMAIL");

/** `useGuidedBrowsingStore.receiveData` delivery method. */
export const channelToDeliveryMethod = (channel: SendChannel): "sms" | "email" =>
    channel === "text" ? "sms" : "email";

/** SMS → E.164 (`+1` + digits, matching legacy `formatInputMDN`); email → trimmed as-is. */
export const toContactValue = (channel: SendChannel, value: string): string =>
    channel === "text" ? `+1${value.replace(/\D/g, "")}` : value.trim();

/** Progressive phone mask `(555) 123-4567` (matches legacy `formatInputMDNDisplay`). Empty → "". */
export const maskMdn = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (!digits) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

/** Send is allowed only for a 10-digit phone (text) or a plausible email (email) — matches legacy. */
export const isValidContact = (channel: SendChannel, value: string): boolean =>
    channel === "text" ? value.replace(/\D/g, "").length === 10 : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
